import { useState } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatShortDay, formatUsd, formatWeekRange } from "./usageFormat";

// Spend per week (bars) against that week's quota (a neutral line with dots),
// values stay in USD; formatMoney converts all visible amounts together.
// Takes the output of fillWeeks: oldest first, the current week
// last. The current week is still running, so its bar is drawn lighter.
//
// Colors are CSS variables so dark mode swaps them in one place. The bar is
// orange-600, which clears the lightness band and 3:1 contrast on both the
// white and the gray-800 surface; the quota line is a gray reference, not a
// second categorical series. Checked with the dataviz palette validator.
const CHART_VARS =
  "[--usage-bar:#ea580c] [--usage-quota:#6b7280] [--usage-surface:#ffffff] [--usage-grid:#e5e7eb] [--usage-tick:#6b7280] " +
  "dark:[--usage-quota:#8b93a1] dark:[--usage-surface:#1f2937] dark:[--usage-grid:#374151] dark:[--usage-tick:#9ca3af]";

function weekTitle(week, language) {
  const range = formatWeekRange(week.week_start, week.week_end, language);
  return language === "zh"
    ? `第 ${week.week_no} 週 · ${range}`
    : `Week ${week.week_no} · ${range}`;
}

function questions(turns, language) {
  if (language === "zh") return `提問 ${turns} 次`;
  return `${turns} ${turns === 1 ? "question" : "questions"}`;
}

// Value first, then what it is measured against. Weeks without AI use have no
// quota on record, so none is shown for them.
function WeekTooltip({ active, payload, language, formatMoney }) {
  if (!active || !payload?.length) return null;
  const week = payload[0].payload.week;
  const zh = language === "zh";
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 shadow-md text-xs">
      <p className="text-gray-500 dark:text-gray-400 mb-1">
        {weekTitle(week, language)}
        {week.isCurrent && (zh ? "（本週）" : " (this week)")}
      </p>
      <p className="text-gray-900 dark:text-white">
        <span className="text-sm font-semibold">{formatMoney(week.used_usd)}</span>{" "}
        {zh ? "已使用" : "used"}
      </p>
      {week.this_week_quota_usd !== null && (
        <p className="text-gray-600 dark:text-gray-300">
          {zh ? "額度" : "Quota"} {formatMoney(week.this_week_quota_usd)}
        </p>
      )}
      <p className="text-gray-600 dark:text-gray-300">
        {week.turns > 0
          ? questions(week.turns, language)
          : zh
            ? "沒有使用 AI"
            : "No AI use"}
      </p>
      {week.over_quota && (
        <p className="mt-1 font-medium text-gray-900 dark:text-white">
          {zh ? "額度已用完" : "Quota used"}
        </p>
      )}
    </div>
  );
}

function HistoryTable({ weeks, language, formatMoney }) {
  const zh = language === "zh";
  const cell = "py-2 pl-2 text-right tabular-nums whitespace-nowrap";
  return (
    <table
      aria-label={zh ? "每週用量歷史" : "Weekly usage history"}
      className="w-full text-sm text-gray-700 dark:text-gray-200"
    >
      <thead>
        <tr className="text-xs text-gray-500 dark:text-gray-400">
          <th className="pb-1 text-left font-medium">{zh ? "週" : "Week"}</th>
          <th className="pb-1 text-right font-medium">{zh ? "已使用" : "Used"}</th>
          <th className="pb-1 text-right font-medium">{zh ? "額度" : "Quota"}</th>
          <th className="pb-1 text-right font-medium">{zh ? "提問" : "Questions"}</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
        {[...weeks].reverse().map((week) => (
          <tr key={week.week_start}>
            <td className="py-2">
              {weekTitle(week, language)}
              {week.isCurrent && (
                <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                  {zh ? "（本週）" : "(this week)"}
                </span>
              )}
            </td>
            <td className={`${cell} font-semibold`}>{formatMoney(week.used_usd)}</td>
            <td className={cell}>{formatMoney(week.this_week_quota_usd)}</td>
            <td className={cell}>{week.turns}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function WeeklyHistoryChart({
  weeks,
  language,
  formatMoney = formatUsd,
}) {
  const [showTable, setShowTable] = useState(false);
  const zh = language === "zh";

  const rows = weeks.map((week) => ({
    key: week.week_start,
    label: week.isCurrent
      ? zh
        ? "本週"
        : "This week"
      : formatShortDay(week.week_start, language),
    used: week.used_usd,
    quota: week.this_week_quota_usd,
    week,
  }));

  return (
    <div className={`flex flex-col gap-3 ${CHART_VARS}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* Legend: the swatch mirrors the mark - a box for bars, a line for the line. */}
        <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600 dark:text-gray-300">
          <span className="inline-flex items-center gap-1.5">
            <span
              className="inline-block w-3 h-3 rounded-sm"
              style={{ background: "var(--usage-bar)" }}
            />
            {zh ? "已使用" : "Used"}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span
              className="inline-block w-4 h-0.5 rounded"
              style={{ background: "var(--usage-quota)" }}
            />
            {zh ? "每週額度" : "Weekly quota"}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setShowTable((v) => !v)}
          className="text-xs font-medium text-orange-600 dark:text-orange-400 hover:underline"
        >
          {showTable
            ? zh
              ? "顯示圖表"
              : "Show chart"
            : zh
              ? "顯示表格"
              : "Show table"}
        </button>
      </div>

      {showTable ? (
        <div className="min-w-0 overflow-x-auto">
          <HistoryTable weeks={weeks} language={language} formatMoney={formatMoney} />
        </div>
      ) : (
        <div>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart
              data={rows}
              margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
            >
              <CartesianGrid vertical={false} stroke="var(--usage-grid)" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "var(--usage-tick)" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                domain={[0, "auto"]}
                tickFormatter={formatMoney}
                tick={{ fontSize: 11, fill: "var(--usage-tick)" }}
                axisLine={false}
                tickLine={false}
                width={formatMoney === formatUsd ? 60 : 96}
              />
              <Tooltip
                cursor={{ fill: "var(--usage-grid)", fillOpacity: 0.5 }}
                content={<WeekTooltip language={language} formatMoney={formatMoney} />}
              />
              <Bar
                dataKey="used"
                fill="var(--usage-bar)"
                radius={[4, 4, 0, 0]}
                maxBarSize={24}
                shape={(props) => (
                  <Rectangle
                    {...props}
                    fillOpacity={props.payload.week.isCurrent ? 0.55 : 1}
                  />
                )}
              />
              <Line
                dataKey="quota"
                type="linear"
                stroke="var(--usage-quota)"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                dot={{
                  r: 4,
                  fill: "var(--usage-quota)",
                  stroke: "var(--usage-surface)",
                  strokeWidth: 2,
                }}
                activeDot={{
                  r: 5,
                  fill: "var(--usage-quota)",
                  stroke: "var(--usage-surface)",
                  strokeWidth: 2,
                }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
